// Which answer on a yes/no question is the Yes, and which is the No.
//
// Lives here rather than inside Question.tsx so the rule can be TESTED against every
// product's real question data without importing a React component — see test:yesno. The
// widget and the test therefore agree by construction instead of by two copies staying in
// step.
//
// Identified by the answer's NAME first, not its code: the codes are not consistently
// derived from the question code (NATURAL_DRAFT_FLOW's answers are NATURAL_DRAFT_YES /
// NATURAL_DRAFT_NO, not NATURAL_DRAFT_FLOW_YES), so a `${qCode}_YES` guess silently fails on
// that one. The code suffix is kept as the fallback, which is what carries Pilot: its answers
// are named "Yes Wire" / "No Wire" and "Yes UV" / "No UV", so an exact name match misses
// them while INCLUDE_WIRE_YES / INCLUDE_WIRE_NO resolve cleanly.

type AnswerLike = { name?: string | null; custrecord_cpqc_answer_code: string };

/** Leading yes/no word in a name: matches "Yes", "yes", "Yes Wire", "No UV". */
const startsWith = (word: "yes" | "no", a: AnswerLike): boolean => {
  const n = String(a.name ?? "").trim().toLowerCase();
  return n === word || n.startsWith(word + " ");
};

export const isYesAnswer = (a: AnswerLike): boolean =>
  startsWith("yes", a) || /(^|_)YES$/i.test(a.custrecord_cpqc_answer_code);

export const isNoAnswer = (a: AnswerLike): boolean =>
  startsWith("no", a) || /(^|_)NO$/i.test(a.custrecord_cpqc_answer_code);

/** Yes first, then No. The records are stored No-then-Yes, which is not how anyone reads a
 *  yes/no question. Returns the input order unchanged when there is no identifiable pair —
 *  with nothing recognised there is no basis for reordering. */
export function yesNoOrder<T extends AnswerLike>(answers: readonly T[]): T[] {
  const yes = answers.find(isYesAnswer);
  const no = answers.find(isNoAnswer);
  return yes && no && yes !== no ? [yes, no] : [...answers];
}
